import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Avatar,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search,
  Visibility,
  Group as GroupIcon,
  People,
  Add,
  Lock,
  Public,
} from '@mui/icons-material';
import adminService from '../../../services/admin.service';

interface University {
  universityId: string;
  universityName: string;
  universityCity: string;
  universityMail: string;
}

interface Group {
  chatId: string;
  groupName: string;
  groupAvatar?: string;
  groupSchoolName: string;
  participants: string[];
  messageCount: number;
  createdAt: string;
  isActive: boolean;
  isPrivateChat?: boolean;
}

const GroupList: React.FC = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalGroups, setTotalGroups] = useState(0);
  const [search, setSearch] = useState('');

  // Universities
  const [universities, setUniversities] = useState<University[]>([]);

  // Create Group Dialog
  const [createDialog, setCreateDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [formData, setFormData] = useState({
    groupName: '',
    groupDescription: '',
    groupAvatar: '',
    groupSchoolName: '',
    isPrivateChat: false,
  });

  useEffect(() => {
    fetchGroups();
    fetchUniversities();
  }, [page, rowsPerPage, search]);

  const fetchGroups = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminService.getAllGroups({
        page: page + 1,
        limit: rowsPerPage,
        search,
      });

      if (response.status) {
        const groupsList = response.data?.groups || [];
        // Backend pagination.totalGroups olarak dönüyor
        const total = response.data?.pagination?.totalGroups || response.data?.pagination?.total || groupsList.length || 0;
        
        setGroups(groupsList);
        setTotalGroups(total);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Gruplar yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUniversities = async () => {
    try {
      const response = await adminService.getInitialConfig();
      if (response.status) {
        setUniversities(response.data?.universities || []);
      }
    } catch (err: any) {
      console.error('Üniversiteler yüklenirken hata oluştu:', err);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleCreateGroup = async () => {
    if (!formData.groupName.trim() || !formData.groupDescription.trim()) {
      alert('Lütfen tüm gerekli alanları doldurun!');
      return;
    }

    setCreateLoading(true);
    try {
      const response = await adminService.createGroup(formData);
      
      if (response.status) {
        alert('Grup başarıyla oluşturuldu!');
        setCreateDialog(false);
        setFormData({
          groupName: '',
          groupDescription: '',
          groupAvatar: '',
          groupSchoolName: '',
          isPrivateChat: false,
        });
        fetchGroups(); // Listeyi yenile
      }
    } catch (err: any) {
      console.error('Create group error:', err);
      alert(err.response?.data?.message || 'Grup oluşturulurken hata oluştu.');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleFormChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Grup Yönetimi
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Tüm aktif grupları görüntüleyin ve yönetin.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialog(true)}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6941a0 100%)',
            },
          }}
        >
          Yeni Grup Oluştur
        </Button>
      </Box>
      <Box sx={{ mb: 4 }} />

      {/* Search */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <TextField
          fullWidth
          placeholder="Grup adına göre ara..."
          value={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell>Grup</TableCell>
                <TableCell>Okul</TableCell>
                <TableCell>Tip</TableCell>
                <TableCell>Üye Sayısı</TableCell>
                <TableCell>Mesaj Sayısı</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Oluşturulma</TableCell>
                <TableCell align="right">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">
                      Grup bulunamadı.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                groups.map((group) => (
                  <TableRow
                    key={group.chatId}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          src={group.groupAvatar}
                          sx={{ bgcolor: '#667eea' }}
                        >
                          <GroupIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {group.groupName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {group.chatId}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{group.groupSchoolName || '-'}</TableCell>
                    <TableCell>
                      {group.isPrivateChat ? (
                        <Chip 
                          icon={<Lock fontSize="small" />}
                          label="Özel" 
                          color="warning" 
                          size="small" 
                        />
                      ) : (
                        <Chip 
                          icon={<Public fontSize="small" />}
                          label="Genel" 
                          color="primary" 
                          size="small" 
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <People fontSize="small" color="action" />
                        {(group as any)?.memberCount || 0}
                      </Box>
                    </TableCell>
                    <TableCell>{group.messageCount || 0}</TableCell>
                    <TableCell>
                      {group.isActive ? (
                        <Chip label="Aktif" color="success" size="small" />
                      ) : (
                        <Chip label="Pasif" color="default" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(group.createdAt).toLocaleDateString('tr-TR')}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => navigate(`/admin/groups/${group.chatId}`)}
                      >
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalGroups}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Sayfa başına satır:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </Paper>

      {/* Create Group Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Yeni Grup Oluştur</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Grup Adı *"
              value={formData.groupName}
              onChange={(e) => handleFormChange('groupName', e.target.value)}
              required
            />
            
            <TextField
              fullWidth
              label="Grup Açıklaması *"
              multiline
              rows={3}
              value={formData.groupDescription}
              onChange={(e) => handleFormChange('groupDescription', e.target.value)}
              required
            />
            
            <TextField
              fullWidth
              label="Grup Avatar URL"
              value={formData.groupAvatar}
              onChange={(e) => handleFormChange('groupAvatar', e.target.value)}
              placeholder="https://example.com/avatar.jpg"
            />
            
            <FormControl fullWidth>
              <InputLabel id="school-name-label">Okul Adı</InputLabel>
              <Select
                labelId="school-name-label"
                value={formData.groupSchoolName}
                onChange={(e) => handleFormChange('groupSchoolName', e.target.value)}
                label="Okul Adı"
              >
                <MenuItem value="">
                  <em>Seçiniz</em>
                </MenuItem>
                {universities.map((university) => (
                  <MenuItem key={university.universityId} value={university.universityName}>
                    {university.universityName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            
            <Box sx={{ 
              p: 2, 
              borderRadius: 2, 
              border: '1px solid #e0e0e0',
              backgroundColor: formData.isPrivateChat ? '#fff3e0' : '#e3f2fd'
            }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPrivateChat}
                    onChange={(e) => handleFormChange('isPrivateChat', e.target.checked)}
                    color={formData.isPrivateChat ? 'warning' : 'primary'}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {formData.isPrivateChat ? (
                      <Lock fontSize="small" color="warning" />
                    ) : (
                      <Public fontSize="small" color="primary" />
                    )}
                    <Box>
                      <Typography variant="body2" fontWeight="600">
                        {formData.isPrivateChat ? 'Özel Grup' : 'Genel Grup'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formData.isPrivateChat 
                          ? 'Sadece davet edilen kullanıcılar katılabilir' 
                          : 'Herkes katılabilir'}
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>
            İptal
          </Button>
          <Button
            onClick={handleCreateGroup}
            variant="contained"
            disabled={createLoading}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            {createLoading ? <CircularProgress size={24} /> : 'Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GroupList;

